import {
  isElseContent,
  isIfCloseContent,
  parseConditionExpression,
  readIfOpenExpression,
} from "@autodromo/template-syntax";
import { evaluateCondition } from "../conditions/evaluateCondition";
import { TEMPLATE_TOKEN_PATTERN } from "../constants";
import { TemplateNode } from "../types";

type IfNode = Extract<TemplateNode, { kind: "if" }>;

export const parseTemplate = (input: string): TemplateNode[] => {
  const root: TemplateNode[] = [];
  const childrenStack: TemplateNode[][] = [root];
  const openIfs: IfNode[] = [];
  const pattern = new RegExp(TEMPLATE_TOKEN_PATTERN.source, "g");

  const current = () => childrenStack[childrenStack.length - 1];
  const pushText = (value: string) => {
    if (value) current().push({ kind: "text", value });
  };

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(input)) !== null) {
    pushText(input.slice(lastIndex, match.index));
    lastIndex = match.index + match[0].length;

    const content = match[1].trim();
    const ifExpression = readIfOpenExpression(content);

    if (ifExpression) {
      const node: IfNode = {
        kind: "if",
        condition: parseConditionExpression({ expression: ifExpression }),
        children: [],
      };
      current().push(node);
      childrenStack.push(node.children);
      openIfs.push(node);
      continue;
    }

    if (isElseContent(content)) {
      const openIf = openIfs[openIfs.length - 1];
      if (openIf) {
        openIf.alternate = openIf.alternate ?? [];
        childrenStack[childrenStack.length - 1] = openIf.alternate;
      }
      continue;
    }

    if (isIfCloseContent(content)) {
      if (openIfs.length > 0) {
        openIfs.pop();
        childrenStack.pop();
      }
      continue;
    }

    current().push({ kind: "var", key: match[1] });
  }
  pushText(input.slice(lastIndex));

  return root;
};

const resolveVar = (key: string, context: Record<string, string>): string => {
  const value = context[key];
  return value !== undefined && value !== null ? String(value) : "";
};

export const renderTemplate = (
  nodes: TemplateNode[],
  context: Record<string, string>,
): string => {
  let out = "";
  let skipLeadingNewline = false;

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];

    if (node.kind === "text") {
      let value = node.value;
      if (skipLeadingNewline) {
        value = value.replace(/^\n/, "");
        skipLeadingNewline = false;
      }
      out += value;
      continue;
    }

    if (node.kind === "var") {
      out += resolveVar(node.key, context);
      skipLeadingNewline = false;
      continue;
    }

    if (evaluateCondition({ condition: node.condition, context })) {
      out += renderTemplate(node.children, context);
      skipLeadingNewline = false;
      continue;
    }

    if (node.alternate) {
      out += renderTemplate(node.alternate, context);
      skipLeadingNewline = false;
      continue;
    }

    const atLineStart = out.length === 0 || out[out.length - 1] === "\n";
    const next = nodes[i + 1];
    const nextStartsNewlineOrEnd =
      !next || (next.kind === "text" && next.value.startsWith("\n"));
    if (atLineStart && nextStartsNewlineOrEnd) {
      skipLeadingNewline = true;
    }
  }

  return out;
};
